// Modal de confirmación para iniciar manualmente la última oportunidad
import React, { useState, useEffect } from 'react';
import { AlertTriangle, PlayCircle, Calendar, Clock } from 'lucide-react';
import BaseModal from './BaseModal';

// Opciones de duración en horas
const DURATION_OPTIONS = [12, 24, 36, 48, 72, 96];

const StartLastChanceModal = ({ property, onConfirm, onCancel }) => {
  // Estado para inicio ahora
  const [startNow, setStartNow] = useState(true);

  // Estado para la fecha/hora de inicio
  const [startDateTime, setStartDateTime] = useState('');

  // Estado para la fecha/hora de cierre
  const [endDateTime, setEndDateTime] = useState('');

  // Función para formatear fecha a datetime-local
  const formatDatetimeLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Función para redondear a la hora siguiente
  const roundToNextHour = (date) => {
    const rounded = new Date(date);
    rounded.setMinutes(0, 0, 0);
    if (date.getMinutes() > 0 || date.getSeconds() > 0) {
      rounded.setHours(rounded.getHours() + 1);
    }
    return rounded;
  };

  // Función para calcular fecha de cierre basada en inicio + horas
  const calculateEndDateTime = (startDate, hours) => {
    const endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + hours);
    return endDate;
  };

  // Inicializar fechas al montar
  useEffect(() => {
    const now = new Date();
    const startDefault = roundToNextHour(now);
    setStartDateTime(formatDatetimeLocal(startDefault));

    // Por defecto: 48 horas desde ahora, redondeado a la hora siguiente
    const endDefault = calculateEndDateTime(roundToNextHour(now), 48);
    setEndDateTime(formatDatetimeLocal(endDefault));
  }, []);

  // Handler para botones de duración
  const handleDurationClick = (hours) => {
    const baseDate = startNow ? roundToNextHour(new Date()) : new Date(startDateTime);
    const endDate = calculateEndDateTime(baseDate, hours);
    setEndDateTime(formatDatetimeLocal(endDate));
  };

  const handleConfirm = () => {
    const selectedStartDate = startNow ? new Date() : new Date(startDateTime);
    const selectedEndDate = new Date(endDateTime);

    // Pasar el deadline exacto en lugar de la duración
    onConfirm(selectedStartDate, selectedEndDate);
  };

  // Validar que la fecha de inicio sea futura o presente (si no es "ahora")
  const isValidStartDate = startNow || new Date(startDateTime) >= new Date(Date.now() - 60000);

  // Validar que la fecha de cierre sea después del inicio
  const getEffectiveStartDate = () => startNow ? new Date() : new Date(startDateTime);
  const isValidEndDate = new Date(endDateTime) > getEffectiveStartDate();

  // Calcular duración actual para mostrar
  const getCurrentDuration = () => {
    if (!endDateTime) return 48; // Default mientras carga
    const start = getEffectiveStartDate();
    const end = new Date(endDateTime);
    const hours = Math.round((end - start) / (1000 * 60 * 60));
    return isNaN(hours) ? 48 : hours;
  };

  const isValidDuration = getCurrentDuration() >= 12 && getCurrentDuration() <= 168;

  return (
    <BaseModal
      onClose={onCancel}
      title="Iniciar Última Oportunidad"
      icon={<PlayCircle className="w-6 h-6 text-emerald-400" />}
      iconBg="bg-emerald-500/20"
      borderColor="border-emerald-500/30"
      maxWidth="lg:max-w-md"
      responsive
      footer={
        <>
          <button
            onClick={onCancel}
            className="flex-1 bg-slate-700 text-white font-medium py-3 rounded-lg hover:bg-slate-600 transition-all"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValidStartDate || !isValidEndDate || !isValidDuration}
            className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold py-3 rounded-lg hover:from-emerald-600 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirmar Inicio
          </button>
        </>
      }
    >
      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-emerald-200 mb-2">
            <strong>¿Iniciar el periodo de última oportunidad?</strong>
          </p>
          <p className="text-xs text-emerald-300/80">
            Esto permitirá a los compradores hacer un último ajuste de su oferta para la propiedad:
          </p>
        </div>
      </div>

      <div className="bg-slate-700/30 rounded-lg p-4">
        <p className="text-white font-semibold text-center">
          {property.title}
        </p>
      </div>

      {/* Selector de fecha/hora de inicio */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-300">
            <Calendar className="w-4 h-4" />
            Fecha y hora de inicio
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={startNow}
              onChange={(e) => setStartNow(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0 bg-slate-700"
            />
            <span className="text-xs text-slate-400">Ahora</span>
          </label>
        </div>
        <input
          type="datetime-local"
          value={startDateTime}
          onChange={(e) => setStartDateTime(e.target.value)}
          disabled={startNow}
          className={`w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:border-emerald-500 ${
            startNow ? 'text-slate-500 opacity-50 cursor-not-allowed' : 'text-white'
          }`}
        />
        <p className="text-xs text-slate-400">
          {startNow ? 'Se iniciará inmediatamente' : 'Cuándo comenzará el periodo de última oportunidad'}
        </p>
        {!isValidStartDate && (
          <p className="text-xs text-red-400">
            La fecha debe ser actual o futura
          </p>
        )}
      </div>

      {/* Fecha de cierre con botones de duración */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-300">
          <Clock className="w-4 h-4" />
          Fecha y hora de cierre
        </label>
        <input
          type="datetime-local"
          value={endDateTime}
          onChange={(e) => setEndDateTime(e.target.value)}
          className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500"
        />

        {/* Botones de duración rápida */}
        <div className="flex flex-wrap gap-2 pt-1">
          {DURATION_OPTIONS.map((hours) => (
            <button
              key={hours}
              type="button"
              onClick={() => handleDurationClick(hours)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                getCurrentDuration() === hours
                  ? 'bg-emerald-500 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              +{hours}h
            </button>
          ))}
        </div>

        <p className="text-xs text-slate-400">
          Duración actual: {getCurrentDuration()}h • Mínimo 12h, máximo 168h (7 días)
        </p>
        {!isValidEndDate && (
          <p className="text-xs text-red-400">
            La fecha de cierre debe ser posterior al inicio
          </p>
        )}
        {!isValidDuration && isValidEndDate && (
          <p className="text-xs text-red-400">
            La duración debe estar entre 12 y 168 horas
          </p>
        )}
      </div>

      <div className="text-sm text-slate-400 space-y-2">
        <p>✅ Los compradores podrán ajustar su oferta UNA vez</p>
        <p>✅ Las ofertas no podrán superar la oferta máxima actual</p>
      </div>
    </BaseModal>
  );
};

export default StartLastChanceModal;
